import React, { useEffect, useRef } from 'react';
import { CopilotHeader } from './components/CopilotHeader';
import Chat, { ChatRef } from './components/Chat';
import { CopilotReportWindow } from './components/CopilotReportWindow';
import { useMemoizedFn } from 'ahooks';
import './index.scss';
import { useAgentStoreContext, useLocalState } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { COPILOT_NEW_SESSION_ID_KEY } from '@/pages/aigc/pages/Agent/config/constant';
import { v4 as uuidv4 } from 'uuid';
import { useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { IReportData } from '../components/ReportViewer/types';
import { useCopilotWindowManager } from './hooks/useCopilotWindowManager';
import { useCopilotWindowLayout } from './hooks/useCopilotWindowLayout';
import CopilotPanelShell from './components/CopilotPanelShell';
import useDSAgentData from '../Home/hooks/useDSAgentData';

const VOICE_BAR_RATIO = 25 / 125; // 比例系数

/** Copilot 根组件 Props */
interface ICopilotProps {
  handleClickRobot?: () => void;
  /** Copilot 会话窗口是否显示（用于区分是共存还是报告窗口单独显示） */
  isShowCopilot?: boolean;
}

/**
 * Copilot 主入口：会话 Chat、报告独立窗、悬浮/内嵌外壳与窗口层级。
 */
const Copilot = ({ handleClickRobot, isShowCopilot = true }: ICopilotProps) => {
  const chatRef = useRef<ChatRef>(null);
  const {
    windowMode,
    defaultWindowMode,
    handleChangeWindowMode,
    handleChangeDefaultWindowMode,
    isDocked,
    dragResizeBoxRef,
    boxWidth,
  } = useCopilotWindowLayout({ isShowCopilot });
  const [sessionId, setSessionId] = useLocalState('__dba_chat_copilot__', '');
  // 记录是否是未开始的新会话,只要发生对话，就设置为旧会话，空字符就代表是旧会话
  const [newSessionID, setNewSessionID] = useLocalState<string>(COPILOT_NEW_SESSION_ID_KEY, '');
  const { fetchChatList, loading: chatListLoading, chatList, bookMarkedChats, refreshAfterChat } = useChatList({manual: false});
  // 在父级请求 DS Agent 数据，避免 Chat 组件因 key 变化导致重复请求
  const { dSExpertAgent, loading: dsAgentLoading } = useDSAgentData();

  // 挂载保护：newSessionID 改为全局 store 后，非正常卸载（如路由切走）可能残留值。
  // 若残留的 newSessionID 与当前 sessionId 不匹配，说明是脏数据，立即清空。
  useEffect(() => {
    if (newSessionID && newSessionID !== sessionId) {
      setNewSessionID('');
    }
    // 仅在挂载时执行一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 缓存 startNewConversation：Chat 卸载后 chatRef.current 为 null，
  // 用独立 ref 保留最后有效引用，确保报告窗口始终可调用
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const startNewConversationRef = useRef<(...args: any[]) => void>(() => {});
  useEffect(() => {
    if (chatRef.current?.startNewConversation) {
      startNewConversationRef.current = chatRef.current.startNewConversation;
    }
  });

  // 使用窗口管理 Hook - 传入 sessionId 确保状态同步
  const {
    dsReport,
    isReportVisible,
    reportSessionId,
    openReportWindow,
    closeReportWindow,
    updateReportData,
    focusCopilot,
    focusReport,
    getCopilotZIndex,
    getReportZIndex,
  } = useCopilotWindowManager({ sessionId });

  // 从全局 store 读取报告所属会话的 conversations，不依赖 chatRef
  // 这样切换 Copilot 会话时，报告窗口的 conversations 不会变化
  const [reportConversations] = useLocalState<unknown[]>(
    `__dba_chat_history_${reportSessionId}__`,
    []
  );
  const { getItem } = useAgentStoreContext();
  const streamingState = getItem(`__dba_chat_history_streaming_state_${reportSessionId}__`);
  const voiceIconWidth = boxWidth - 250;
  const voiceActiveBar = Math.round(voiceIconWidth * VOICE_BAR_RATIO);

  /** 切换当前 Copilot 会话 */
  const handleChangeCopilotSession = useMemoizedFn((selectedSessionId: string) => {
    setNewSessionID('');
    setSessionId(selectedSessionId);
  });

  /** 创建未开始对话的新 session */
  const createNewSession = useMemoizedFn(() => {
    const newID = uuidv4();
    setNewSessionID(newID);
    setSessionId(newID);
  });

  const changeSessionIdToOld = () => {
    setNewSessionID('');
  };

  /** 关闭 Copilot 会话窗口并重置 session 状态 */
  const handleCloseCopilot = useMemoizedFn(() => {
    // 关闭会话窗口时，重置 Copilot 会话相关状态，
    // 确保下次打开时回到默认态（新的 sessionId）
    setNewSessionID('');
    setSessionId('');
    handleClickRobot?.();
  });

  /** 点击 DS 文件卡片：打开报告窗并置顶 */
  const handleFileCardClick = useMemoizedFn((dsReportData: IReportData) => {
    // 打开报告窗口并设置为激活状态（置顶）
    // 记录当前 Copilot 会话 ID，作为该报告的 sourceSessionId
    openReportWindow({
      ...dsReportData,
      sourceSessionId: sessionId,
    });
    // 确保报告窗口获得焦点，置于最顶层
    // 延迟执行以确保状态已更新
    setTimeout(() => {
      focusReport();
    }, 0);
  });

  useEffect(() => {
    if (!sessionId) {
      createNewSession();
    }
  }, [sessionId]);

  return (
    <>
      {/* Copilot 主窗口 - 根据 isShowCopilot 判断是否显示 */}
      {isShowCopilot && (
        <CopilotPanelShell
          isDocked={isDocked}
          ref={dragResizeBoxRef}
          zIndex={getCopilotZIndex()}
          onResizeStart={focusCopilot}
        >
          <div onClick={focusCopilot} onMouseDown={focusCopilot} style={{ height: '100%' }}>
            {/* 头部 */}
            <CopilotHeader
              {...{
                chatList,
                bookMarkedChats,
                chatListLoading,
                createNewSession,
                handleCloseCopilot,
                newSessionID,
                dragResizeBoxRef,
                handleChangeCopilotSession,
                sessionId,
                onFocus: focusCopilot,
                isReportExpanded: isReportVisible,
                closeReportWindow,
                reportSessionId,
                dsReport,
                windowMode,
                onChangeWindowMode: handleChangeWindowMode,
                defaultWindowMode,
                onChangeDefaultWindowMode: handleChangeDefaultWindowMode,
              }}
            />
            {sessionId && <Chat
              changeSessionIdToOld={changeSessionIdToOld}
              chatList={chatList}
              bookMarkedChats={bookMarkedChats}
              key={sessionId}
              sessionId={sessionId}
              newSessionID={newSessionID}
              ref={chatRef}
              voiceActiveBar={voiceActiveBar}
              voiceIconWidth={voiceIconWidth}
              dropAreaRef={dragResizeBoxRef.current?.dropAreaRef}
              fetchChatList={fetchChatList}
              onFileCardClick={handleFileCardClick}
              dSExpertAgent={dSExpertAgent}
              dsAgentLoading={dsAgentLoading}
              /** 传递给 AiTableTabContent，用于直接渲染 ChatSmartTable */
              dsReport={dsReport as any}
              reportSessionId={reportSessionId}
              reportConversations={reportConversations}
              streamingState={streamingState?.current || ''}
              handleUpdateDsReport={updateReportData}
              startNewConversationRef={startNewConversationRef}
              /** 用于自动打开新生成的表格和海报文件 */
              openReportWindow={openReportWindow}
              refreshAfterChat={refreshAfterChat}
            />}
          </div>
        </CopilotPanelShell>
      )}

      {/* DS 报告独立窗口 - 使用独立的 reportSessionId */}
      {isReportVisible && (
        <CopilotReportWindow
          visible={isReportVisible}
          sessionId={reportSessionId}
          conversations={reportConversations}
          dsReport={dsReport as IReportData}
          streamingState={streamingState?.current || ''}
          handleUpdateDsReport={updateReportData}
          startNewConversation={startNewConversationRef.current}
          onFocus={focusReport}
          zIndex={getReportZIndex()}
          onClose={closeReportWindow}
        />
      )}
    </>
  );
};

export default Copilot;
